import { useStaggeredAnimation } from "@/hooks/useScrollAnimation";
import { ClipboardCheck, Package, Truck, Scale, Users } from "lucide-react";
import { FeaturesCTA } from "./FeaturesCTA";

const valueProps = [
  {
    icon: ClipboardCheck,
    title: "Plan Smarter",
    description: "Design weekly menus around your camper headcounts, dietary needs, and serving sizes — no more guesswork."
  },
  {
    icon: Package,
    title: "Track Inventory in Real Time",
    description: "Know exactly what's in your kitchen, what's running low, and what's already covered for next week's meals."
  },
  {
    icon: Truck,
    title: "Order with Confidence",
    description: "Generate Sysco-ready purchase orders automatically. No CSV uploads, no manual math — just one click."
  },
  {
    icon: Scale,
    title: "Reduce Waste, Save Money",
    description: "CampChef Pro helps you balance portions and minimize leftovers, saving hundreds each season."
  },
  {
    icon: Users,
    title: "Empower Your Kitchen Team",
    description: "Give chefs, volunteers, and staff shared access to recipes, inventory, and daily meal schedules in one platform."
  }
];

export const ValueProposition = () => {
  const { containerRef, visibleItems } = useStaggeredAnimation(valueProps.length, 150);

  return (
    <section id="features" className="py-20 bg-[#FFFDF6] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-0 w-64 h-64 bg-deep-teal rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-copper rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-slate mb-4">
            Everything You Need to Run Your Camp Kitchen Smoothly
          </h2>
          <p className="font-inter text-lg text-slate/75 max-w-3xl mx-auto">
            From menu planning to Sysco ordering, CampChef Pro helps you serve great meals — without the chaos.
          </p>
        </div>

        <div ref={containerRef} className="space-y-8">
          {/* First 4 features in 4-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {valueProps.slice(0, 4).map((prop, index) => {
              const Icon = prop.icon;
              return (
                <div
                  key={index}
                  className={`bg-white/95 rounded-2xl p-8 border-2 border-deep-teal/20 shadow-lg shadow-slate/10 transition-all duration-500 hover:shadow-xl hover:shadow-deep-teal/20 hover:border-deep-teal/40 hover:-translate-y-1 focus-within:ring-2 focus-within:ring-deep-teal focus-within:ring-offset-2 ${
                    visibleItems.has(index)
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-12'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="flex justify-center mb-6">
                    <div className="bg-deep-teal/10 border-2 border-deep-teal/30 p-4 rounded-xl" aria-label={`${prop.title} icon`}>
                      <Icon className="h-8 w-8 text-deep-teal" strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3 className="font-playfair text-xl font-semibold text-slate mb-3 text-center">
                    {prop.title}
                  </h3>
                  <p className="font-inter text-slate/75 text-center text-sm leading-relaxed">
                    {prop.description}
                  </p>
                </div>
              );
            })}
          </div>
          
          {/* 5th feature centered below on desktop */}
          <div className="flex justify-center">
            {(() => {
              const prop = valueProps[4];
              const Icon = prop.icon;
              return (
                <div
                  className={`bg-white/95 rounded-2xl p-8 border-2 border-deep-teal/20 shadow-lg shadow-slate/10 transition-all duration-500 hover:shadow-xl hover:shadow-deep-teal/20 hover:border-deep-teal/40 hover:-translate-y-1 focus-within:ring-2 focus-within:ring-deep-teal focus-within:ring-offset-2 w-full md:w-1/2 lg:w-1/4 ${
                    visibleItems.has(4)
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-12'
                  }`}
                  style={{ transitionDelay: `${4 * 150}ms` }}
                >
                  <div className="flex justify-center mb-6">
                    <div className="bg-deep-teal/10 border-2 border-deep-teal/30 p-4 rounded-xl" aria-label={`${prop.title} icon`}>
                      <Icon className="h-8 w-8 text-deep-teal" strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3 className="font-playfair text-xl font-semibold text-slate mb-3 text-center">
                    {prop.title}
                  </h3>
                  <p className="font-inter text-slate/75 text-center text-sm leading-relaxed">
                    {prop.description}
                  </p>
                </div>
              );
            })()}
          </div>
        </div>

        <FeaturesCTA />
      </div>
    </section>
  );
};
